import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { GridCellParams, GridRowParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { NxObjectType } from '@warehouse/object-browser/core';
import Button from '../../../../src/components/library/Button';
import Tooltip from '../../../../src/components/library/Tooltip';
import { CustomDataGridPro } from '../../../shared/ui/table/CustomDataGridPro';
import { BrokerJob } from '../core';
import { brokerJobsStoreSelector, useBrokerJobsStore } from '../domain/broker-jobs.store';
import { createBrokerJobsColumns } from './columns';
import { JobDetailPanel } from './fields';
import { NoJobsOverlay } from './NoJobsOverlay';
import { TriggerServiceMenu } from './TriggerServiceMenu';

interface JobsPanelProps {
	nxObjectUuid: string;
	itemType: NxObjectType;
}

const Toolbar = styled('div')(({ theme }) => ({
	alignItems: 'center',
	display: 'flex',
	gap: theme.spacing(1.25),
	marginBottom: theme.spacing(1.75),
}));

const JobCountLabel = styled('span')(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontSize: theme.typography.bodySmallRegular.fontSize,
}));

const ToolbarSpacer = styled('div')({
    flex: 1,
});

const SpinningRefreshIcon = styled(RefreshIcon)<{ $spinning: boolean }>(
    ({ $spinning }) =>	$spinning
		? {
			animation: 'nx-broker-refresh-spin 700ms linear infinite',

			'@keyframes nx-broker-refresh-spin': {
				to: {
					transform: 'rotate(360deg)',
				},
			},
		}	: {},
);

const RefreshButton = styled(Button)(({ theme }) => ({
    minWidth: '36px',
    padding: 0,
    width: '36px',

    '&, &:hover': {
        backgroundColor: theme.palette.light.background,
    },

    '&:focus': {
        backgroundColor: theme.palette.light.main,
    },
}));

const TableWrapper = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	minHeight: '100px',

	'& .MuiDataGrid-row:hover, & .MuiDataGrid-row:hover .MuiDataGrid-cell, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight': {
		backgroundColor: `${theme.palette.light.background} !important`,
	},

	'& .even > .MuiDataGrid-cell, & .odd > .MuiDataGrid-cell': {
		backgroundColor: `${theme.palette.white.main} !important`,
	},
}));

const DetailPanelExpandIcon = styled(ChevronRightIcon)(({ theme }) => ({
	color: theme.palette.text.secondary,
}));

const DetailPanelCollapseIcon = styled(ChevronRightIcon)(({ theme }) => ({
	color: theme.palette.text.secondary,
	transform: 'rotate(90deg)',
}));

export function JobsPanel({ nxObjectUuid, itemType }: JobsPanelProps) {
	const jobs = useBrokerJobsStore(brokerJobsStoreSelector.jobsFor(nxObjectUuid));
	const apiRef = useGridApiRef();
	const isRefreshing = useBrokerJobsStore(brokerJobsStoreSelector.isRefreshing(nxObjectUuid));
	const { refresh } = useBrokerJobsStore(brokerJobsStoreSelector.actions);
	const { subscribe, unsubscribe } = useBrokerJobsStore(brokerJobsStoreSelector.internalActions);

	useEffect(() => {
		subscribe(nxObjectUuid);
		return () => unsubscribe(nxObjectUuid);
	}, [nxObjectUuid, subscribe, unsubscribe]);

	const columns = useMemo(() => createBrokerJobsColumns(nxObjectUuid), [nxObjectUuid]);
	const getDetailPanelContent = useCallback(({ row }: GridRowParams<BrokerJob>) => <JobDetailPanel job={row} />, []);
	const getDetailPanelHeight = useCallback(() => 'auto', []);
	const handleCellClick = useCallback(
		(params: GridCellParams<BrokerJob>) => {
			if (params.field.startsWith('__')) return;
			apiRef.current.toggleDetailPanel(params.id);
		},
		[apiRef],
	);

	return (
		<div>
			<Toolbar>
				<JobCountLabel>{jobs.length === 1 ? '1 job' : `${jobs.length} jobs`}</JobCountLabel>
				<ToolbarSpacer />
				<Tooltip title="Refresh" >
					<RefreshButton
						type="button"
						nxstyle="normal"
						aria-label="Refresh-Button"
						onClick={() => refresh(nxObjectUuid)}
					>
						<SpinningRefreshIcon $spinning={isRefreshing} fontSize="small" />
					</RefreshButton>
				</Tooltip>
				<TriggerServiceMenu nxObjectUuid={nxObjectUuid} itemType={itemType} />
			</Toolbar>

			{jobs.length === 0 ? (
				<NoJobsOverlay />
			) : (
				<TableWrapper>
					<CustomDataGridPro<BrokerJob>
						$variant="inline"
						apiRef={apiRef}
						autoHeight
						hideFooter
						disableRowSelectionOnClick
						getRowId={(row) => `${row.jobId}:${row.startedAt}`}
						columns={columns}
						rows={jobs}
						getDetailPanelContent={getDetailPanelContent}
						getDetailPanelHeight={getDetailPanelHeight}
						onCellClick={handleCellClick}
						slots={{
							detailPanelExpandIcon: DetailPanelExpandIcon,
							detailPanelCollapseIcon: DetailPanelCollapseIcon,
						}}
					/>
				</TableWrapper>
			)}
		</div>
	);
}
